import type { TOC } from '@ember/component/template-only';
import type { SafeString } from '@ember/template';

/**
 * Intent / color family
 */
export type CalloutIntent = 'brand' | 'success' | 'warning' | 'info' | 'danger';

interface CalloutSignature {
  Args: {
    /**
     * Visual intent — picks the token family (brand / success / warning /
     * info / danger). Defaults to `brand`.
     */
    intent?: CalloutIntent;
    /** Uses a two-stop tinted gradient instead of a flat fill. Defaults to false. */
    gradient?: boolean;
    /**
     * Use the saturated intent-gradient icon bubble with white iconography
     * (for promotional/CTA callouts where the icon should pop). Defaults
     * to `false` — uses the soft tinted bubble matching the intent body.
     */
    iconBold?: boolean;
    /**
     * Pull the callout up to overlap the element above it (used to stitch
     * a callout into the bottom of a hero). Applies `-mt-6 mx-6` with
     * `isolation: isolate` so the callout creates its own stacking
     * context and paints above the prior sibling without needing a
     * `z-index` value at the call site. Defaults to `false`.
     */
    overlap?: boolean;
    /**
     * Vertical alignment of the icon | content | action triad. Defaults
     * to `start` (matches `flex items-start`). Pass `center` for callouts
     * with single-line bodies.
     */
    align?: 'start' | 'center';
    /**
     * Optional title for the callout. Rendered as an `<h3>` carrying
     * `.tio-callout__title` (intent-driven color + bottom margin). Pass
     * a title here or render your own heading inside the default block —
     * not both.
     */
    title?: string | SafeString;
  };
  Blocks: {
    /**
     * Main content of the callout — body paragraphs, chips, anything
     * that isn't the icon or the action slot.
     */
    default: [];
    /**
     * Icon slot — typically a `<Ph…>` icon component. Wrapped in a
     * `.tio-callout__icon` (or `+ __icon--bold`) bubble.
     */
    icon?: [];
    /**
     * Trailing action — usually a `<Button>` (with `@route` when it navigates).
     * Renders to the right of the content on desktop.
     */
    action?: [];
  };
  Element: HTMLDivElement;
}

/** Tinted content card with a CTA slot and no implicit alert role. */
const Callout: TOC<CalloutSignature> = <template>
  <div
    class="tio-callout tio-callout--{{if @intent @intent 'brand'}}
      {{if @gradient 'tio-callout--gradient'}}
      {{if @overlap 'tio-callout--overlap'}}
      {{if (eq @align 'center') 'items-center'}}"
    ...attributes
  >
    {{#if (has-block "icon")}}
      <div class="tio-callout__icon {{if @iconBold 'tio-callout__icon--bold'}}">
        {{yield to="icon"}}
      </div>
    {{/if}}
    <div class="flex-1">
      {{#if @title}}
        <h3 class="tio-callout__title">{{@title}}</h3>
      {{/if}}
      {{yield}}
    </div>
    {{#if (has-block "action")}}
      {{yield to="action"}}
    {{/if}}
  </div>
</template>;

export default Callout;
export type { CalloutSignature };
